import { RuleConditionType } from "../../types/transactionRule";
import {
    IndexedCondition,
    IndexedRule,
    CATEGORY_TYPES,
    ARRAY_SET_TYPES,
    ROUNDING_TYPES,
    areSetsEqual,
    isRoundingValue,
    isAmountValue,
    isDateValue,
    areRoundingValuesEqual,
    areSimilarToValuesEqual,
} from "./types";

export function isSameTypeConditionIdentical(
    lower: IndexedCondition,
    higherSameType: readonly IndexedCondition[],
): boolean {
    if (higherSameType.length === 0) {
        return false;
    }
    const type = lower.condition.conditionType;

    if (type === RuleConditionType.ENTITY) {
        const lowerSet = lower.scopeEntities ?? new Set<number>();
        return higherSameType.every((h) =>
            areSetsEqual(lowerSet, h.scopeEntities ?? new Set()),
        );
    }
    if (type === RuleConditionType.FINANCIAL_ACCOUNTS) {
        const lowerSet = lower.scopeAccounts ?? new Set<number>();
        return higherSameType.every((h) =>
            areSetsEqual(lowerSet, h.scopeAccounts ?? new Set()),
        );
    }
    if (type === RuleConditionType.SUBACCOUNTS) {
        const lowerSet = lower.scopeSubaccounts ?? new Set<string>();
        return higherSameType.every((h) =>
            areSetsEqual(lowerSet, h.scopeSubaccounts ?? new Set()),
        );
    }

    if (CATEGORY_TYPES.has(type)) {
        const lowerRaw = lower.categoryRaw ?? new Set<string>();
        return higherSameType.every((h) =>
            areSetsEqual(lowerRaw, h.categoryRaw ?? new Set()),
        );
    }

    if (type === RuleConditionType.SIMILAR_TO) {
        const l = lower.similarTo;
        if (!l) {
            return false;
        }
        return higherSameType.every(
            (h) =>
                h.similarTo !== undefined &&
                areSimilarToValuesEqual(l, h.similarTo),
        );
    }

    if (ARRAY_SET_TYPES.has(type)) {
        const lowerSet = new Set(lower.values);
        return higherSameType.every((h) =>
            areSetsEqual(lowerSet, new Set(h.values)),
        );
    }

    if (type === RuleConditionType.AMOUNT) {
        const l = lower.condition.value;
        if (!isAmountValue(l)) {
            return false;
        }
        return higherSameType.every((h) => {
            const r = h.condition.value;
            return isAmountValue(r) && l.min === r.min && l.max === r.max;
        });
    }

    if (type === RuleConditionType.DATE) {
        const l = lower.condition.value;
        if (!isDateValue(l)) {
            return false;
        }
        return higherSameType.every((h) => {
            const r = h.condition.value;
            return (
                isDateValue(r) && l.after === r.after && l.before === r.before
            );
        });
    }

    if (type === RuleConditionType.DATE_PROXIMITY) {
        const l = lower.condition.value;
        if (typeof l !== "number") {
            return false;
        }
        return higherSameType.every((h) => {
            const r = h.condition.value;
            return typeof r === "number" && r === l;
        });
    }

    if (ROUNDING_TYPES.has(type)) {
        const l = lower.condition.value;
        if (!isRoundingValue(l)) {
            return false;
        }
        return higherSameType.every((h) => {
            if (!isRoundingValue(h.condition.value)) {
                return false;
            }
            return areRoundingValuesEqual(l, h.condition.value);
        });
    }

    return false;
}

export function isRuleDuplicateAgainst(
    lower: IndexedRule,
    higher: IndexedRule,
): boolean {
    if (lower.conditionBucketKeys.size !== higher.conditionBucketKeys.size) {
        return false;
    }
    for (const key of lower.conditionBucketKeys) {
        if (!higher.conditionBucketKeys.has(key)) {
            return false;
        }
    }
    for (const [key, lowerConditions] of lower.buckets) {
        const higherConditions = higher.buckets.get(key);
        if (!higherConditions) {
            return false;
        }
        if (lowerConditions.length !== higherConditions.length) {
            return false;
        }
        for (const l of lowerConditions) {
            if (!isSameTypeConditionIdentical(l, higherConditions)) {
                return false;
            }
        }
    }
    return true;
}
